import { Fragment } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FeaturesSectionEditor } from "./sections/FeaturesSectionEditor";
import { NarrationTracksEditor } from "./sections/NarrationTracksEditor";
import { FacetsEditor } from "./sections/FacetsEditor";
import { RelatedRefsEditor } from "./sections/RelatedRefsEditor";
import { CountLabelsEditor } from "./sections/CountLabelsEditor";
import { CatalogTermsEditor } from "./sections/CatalogTermsEditor";
import { SectionsFromEditor } from "./sections/SectionsFromEditor";
import { RecordBooleanEditor } from "./sections/RecordBooleanEditor";
import { FieldDescriptor, RenderField, fieldValue, withField } from "./fieldTypes";

/**
 * Groups `fields` by `descriptor.section`, preserving first-seen order (the order
 * the registry itself lists them in) — one heading per group.
 */
function groupBySection(fields: RenderField[]): { section: string; fields: RenderField[] }[] {
  const order: string[] = [];
  const by = new Map<string, RenderField[]>();
  for (const f of fields) {
    if (!by.has(f.descriptor.section)) {
      order.push(f.descriptor.section);
      by.set(f.descriptor.section, []);
    }
    by.get(f.descriptor.section)!.push(f);
  }
  return order.map((section) => ({ section, fields: by.get(section)! }));
}

/**
 * The one generic properties-panel renderer every node type (Catalog/Pack/Lesson/
 * CatalogPackEntry/CatalogNodeEntry) shares (docs/343-ADMIN-MANIFEST-BUILDER-
 * REDESIGN.md v0 §2/§7) — dispatches on `FieldDescriptor.kind`. `value`/`onChange`
 * are the WHOLE draft object for the selected node; each control reads/writes its
 * own key via `fieldValue`/`withField` (immutable — `onChange` always receives a
 * brand new object).
 */
export function FieldForm({
  fields,
  value,
  onChange,
  onBrowseFile,
}: {
  fields: RenderField[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  onBrowseFile?: (key: string) => void;
}) {
  const groups = groupBySection(fields);
  return (
    <Stack spacing={3}>
      {groups.map((g) => (
        <Box key={g.section}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {g.section}
          </Typography>
          <Stack spacing={1.5}>
            {g.fields.map((f) => (
              <Fragment key={f.key}>
                <FieldControl
                  descriptor={f.descriptor}
                  value={fieldValue(value, f.key)}
                  onChange={(v) => onChange(withField(value, f.key, v))}
                  onBrowseFile={f.descriptor.filePicker && onBrowseFile ? () => onBrowseFile(f.key) : undefined}
                />
              </Fragment>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const UNSET = "__unset__";

function FieldControl({
  descriptor,
  value,
  onChange,
  onBrowseFile,
}: {
  descriptor: FieldDescriptor;
  value: unknown;
  onChange: (v: unknown) => void;
  onBrowseFile?: () => void;
}) {
  switch (descriptor.kind) {
    case "text":
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            size="small"
            label={descriptor.label}
            helperText={descriptor.help}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
          />
          {onBrowseFile ? (
            <Button size="small" variant="outlined" onClick={onBrowseFile} sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
              Browse…
            </Button>
          ) : null}
        </Box>
      );
    case "textarea":
      return (
        <TextField
          fullWidth
          multiline
          minRows={2}
          size="small"
          label={descriptor.label}
          helperText={descriptor.help}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : e.target.value)}
        />
      );
    case "number": {
      const num = typeof value === "number" ? String(value) : "";
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={descriptor.label}
          helperText={descriptor.help}
          value={num}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(undefined);
            const n = descriptor.integer ? parseInt(raw, 10) : Number(raw);
            onChange(Number.isFinite(n) ? n : undefined);
          }}
        />
      );
    }
    case "boolean3":
      return <Boolean3Field label={descriptor.label} help={descriptor.help} value={value} onChange={onChange} />;
    case "select": {
      const current = typeof value === "string" ? value : UNSET;
      return (
        <TextField
          select
          fullWidth
          size="small"
          label={descriptor.label}
          helperText={descriptor.help}
          value={current}
          onChange={(e) => onChange(e.target.value === UNSET ? undefined : e.target.value)}
        >
          <MenuItem value={UNSET}>
            <em>(unset)</em>
          </MenuItem>
          {(descriptor.options ?? []).map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    case "stringArray": {
      const arr = Array.isArray(value) ? (value as unknown[]).filter((v) => typeof v === "string") : [];
      return (
        <TextField
          fullWidth
          size="small"
          label={descriptor.label}
          helperText={descriptor.help ?? "Comma-separated"}
          value={arr.join(", ")}
          onChange={(e) => {
            const parts = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            onChange(parts.length > 0 ? parts : undefined);
          }}
        />
      );
    }
    case "recordBoolean":
      return <RecordBooleanEditor label={descriptor.label} help={descriptor.help} value={value} onChange={onChange} />;
    case "featureFlags":
      return <FeaturesSectionEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "narrationTracks":
      return <NarrationTracksEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "facets":
      return <FacetsEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "relatedRefs":
      return <RelatedRefsEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "countLabels":
      return <CountLabelsEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "catalogTerms":
      return <CatalogTermsEditor label={descriptor.label} value={value} onChange={onChange} />;
    case "sectionsFrom":
      return <SectionsFromEditor label={descriptor.label} help={descriptor.help} value={value} onChange={onChange} />;
    case "readonly":
      return (
        <Box>
          <Typography variant="caption" color="text.secondary">
            {descriptor.label}
            {descriptor.help ? ` — ${descriptor.help}` : ""}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              p: 1,
              bgcolor: "action.hover",
              borderRadius: 1,
              fontSize: 12,
              overflowX: "auto",
              maxHeight: 200,
            }}
          >
            {value === undefined ? "(none)" : JSON.stringify(value, null, 2)}
          </Box>
        </Box>
      );
    default:
      return null;
  }
}

/** A tri-state control — "(use default)" / On / Off — for an optional boolean field
 *  (`undefined` must stay representable; a plain `Switch` can't). */
function Boolean3Field({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const current = value === true ? "true" : value === false ? "false" : UNSET;
  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      helperText={help}
      value={current}
      onChange={(e) => onChange(e.target.value === UNSET ? undefined : e.target.value === "true")}
    >
      <MenuItem value={UNSET}>
        <em>(use default)</em>
      </MenuItem>
      <MenuItem value="true">On</MenuItem>
      <MenuItem value="false">Off</MenuItem>
    </TextField>
  );
}

/** Shared "boolean checkbox that's genuinely required, not tri-state" — used inside
 *  a couple of the structured sub-editors (e.g. `CatalogTermsEditor`'s `required`
 *  flag reads more naturally as a plain checkbox than a 3-state select there too;
 *  kept here so those files don't each redeclare it). */
export function InlineCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <FormControlLabel
      control={<Checkbox size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
      label={label}
    />
  );
}
